/**
 * UniversalEntityCard — canonical public Entity Space card foundation.
 *
 * One visual shell serves Profile, Katalag, and BrandStore identities while
 * keeping their metric and action policies typed. Missing data is omitted;
 * the component never manufactures zero metrics, ranks, flags, or actions.
 */
import React from 'react';
import { LibraryBig, Store, UserRound } from 'lucide-react';
import { cn } from '../../../lib/utils';

export type EntityCardKind = 'profile' | 'katalag' | 'brandstore';

export type KloutscoreProvenance = 'verified' | 'self-reported' | 'unverified' | 'pending';

export interface EntityCardAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export interface EntityCardData {
  id: string;
  kind: EntityCardKind;
  title: string;
  handle?: string | null;
  coverImageUrl?: string | null;
  avatarUrl?: string | null;
  /** Lifestyle imagery uses cover; isolated product-style imagery may opt into contain. */
  mediaFit?: 'cover' | 'contain';
  about?: string | null;
  bestCategory?: string | null;
  countryCode?: string | null;

  // Profile / Katalag metrics
  reach?: number | null;
  kloutscore?: number | null;
  kloutscoreSource?: string | null;
  kloutscoreVerifiedAt?: string | null;
  rank?: number | null;
  rankTotal?: number | null;

  // BrandStore metrics
  itemsSold?: number | null;
  totalSalesAmount?: number | null;
  currencyCode?: string | null;
}

export interface UniversalEntityCardProps {
  entity: EntityCardData;
  onOpen?: (entityId: string) => void;
  primaryAction?: EntityCardAction;
  secondaryAction?: EntityCardAction;
  className?: string;
}

const VERIFIED_SOURCES = new Set(['verified', 'api', 'sync']);
const SELF_REPORTED_SOURCES = new Set(['manual', 'user_input', 'self_reported', 'self-reported']);

/**
 * Verification is source-authoritative. A timestamp alone is deliberately
 * ignored because historical manual rows may also carry a timestamp.
 */
export function resolveKloutscoreProvenance(
  kloutscore: number | null | undefined,
  source: string | null | undefined,
  _verifiedAt?: string | null,
): KloutscoreProvenance {
  if (kloutscore == null) return 'pending';

  const normalized = source?.trim().toLowerCase() ?? '';
  if (VERIFIED_SOURCES.has(normalized)) return 'verified';
  if (SELF_REPORTED_SOURCES.has(normalized)) return 'self-reported';
  return 'unverified';
}

export function formatEntityCount(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(value >= 100_000 ? 0 : 1)}K`;
  }
  return String(value);
}

function countryFlag(countryCode?: string | null): string {
  if (!countryCode || !/^[A-Za-z]{2}$/.test(countryCode)) return '';
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(code.charCodeAt(0) + 127397, code.charCodeAt(1) + 127397);
}

function formatSales(amount: number, currencyCode?: string | null): string {
  if (!currencyCode) return amount.toLocaleString('en-US', { maximumFractionDigits: 0 });

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode.toUpperCase()} ${amount.toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })}`;
  }
}

function entityLabel(kind: EntityCardKind): string {
  if (kind === 'brandstore') return 'BrandStore';
  if (kind === 'katalag') return 'Katalag';
  return 'Profile';
}

function EntityIcon({ kind, className }: { kind: EntityCardKind; className?: string }) {
  if (kind === 'brandstore') return <Store className={className} />;
  if (kind === 'katalag') return <LibraryBig className={className} />;
  return <UserRound className={className} />;
}

interface MetricView {
  key: string;
  label: string;
  value: string;
  note?: string;
}

function buildMetrics(entity: EntityCardData): MetricView[] {
  if (entity.kind === 'brandstore') {
    const metrics: MetricView[] = [];
    if (entity.itemsSold != null) {
      metrics.push({
        key: 'items-sold',
        label: 'Items Sold',
        value: formatEntityCount(entity.itemsSold),
      });
    }
    if (entity.totalSalesAmount != null) {
      metrics.push({
        key: 'total-sales',
        label: 'Total Sales',
        value: formatSales(entity.totalSalesAmount, entity.currencyCode),
      });
    }
    return metrics;
  }

  const metrics: MetricView[] = [];
  if (entity.reach != null) {
    metrics.push({ key: 'reach', label: 'Reach', value: formatEntityCount(entity.reach) });
  }

  if (entity.kloutscore != null) {
    const provenance = resolveKloutscoreProvenance(
      entity.kloutscore,
      entity.kloutscoreSource,
      entity.kloutscoreVerifiedAt,
    );
    const note =
      provenance === 'verified'
        ? 'Verified source'
        : provenance === 'self-reported'
          ? 'Self-reported'
          : 'Source unverified';

    metrics.push({
      key: 'kloutscore',
      label: 'Kloutscore',
      value: String(entity.kloutscore),
      note,
    });
  }

  if (entity.rank != null && entity.rankTotal != null && entity.rank > 0 && entity.rankTotal > 0) {
    metrics.push({
      key: 'rank',
      label: 'Rank',
      value: `${entity.rank}/${entity.rankTotal}`,
    });
  }

  return metrics;
}

export const UniversalEntityCard: React.FC<UniversalEntityCardProps> = ({
  entity,
  onOpen,
  primaryAction,
  secondaryAction,
  className,
}) => {
  const metrics = buildMetrics(entity);
  const flag = countryFlag(entity.countryCode);
  const title = entity.title.trim() || entityLabel(entity.kind);
  const handle = entity.handle?.trim().replace(/^@/, '') || null;
  const about = entity.about?.trim() || null;
  const category = entity.bestCategory?.trim() || null;
  const mediaFitClass = entity.mediaFit === 'contain' ? 'object-contain p-6' : 'object-cover';
  const initials = title
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const isInteractive = typeof onOpen === 'function';
  const gridColumns =
    metrics.length === 1 ? 'grid-cols-1' : metrics.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
  const actionCount = Number(Boolean(secondaryAction)) + Number(Boolean(primaryAction));
  const actionGridColumns = actionCount === 1 ? 'grid-cols-1' : 'grid-cols-2';

  const handleOpen = () => onOpen?.(entity.id);
  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button, a, [data-no-nav]')) return;
    handleOpen();
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isInteractive || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    handleOpen();
  };

  return (
    <article
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow',
        isInteractive &&
          'cursor-pointer hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      data-canonical-entity-card="true"
      data-entity-kind={entity.kind}
      data-entity-id={entity.id}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-primary/20 via-muted to-accent/20">
        {entity.coverImageUrl ? (
          <img
            src={entity.coverImageUrl}
            alt=""
            className={cn('h-full w-full', mediaFitClass)}
            data-canonical-entity-cover="true"
            data-canonical-media-fit={entity.mediaFit ?? 'cover'}
          />
        ) : (
          <div
            className="relative flex h-full w-full items-center justify-center overflow-hidden"
            data-canonical-entity-cover-fallback="true"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-muted to-accent/25" />
            <div className="relative flex flex-col items-center gap-4 text-primary/30">
              <EntityIcon kind={entity.kind} className="h-20 w-20" />
              <span className="text-5xl font-black tracking-[0.12em]">{initials || '?'}</span>
            </div>
          </div>
        )}
      </div>

      <div className="relative flex items-center justify-between px-5" aria-hidden="true">
        <div className="absolute bottom-0 left-5 translate-y-1/2">
          {entity.avatarUrl ? (
            <div className="h-14 w-14 overflow-hidden rounded-full border-[3px] border-card bg-card shadow-lg">
              <img src={entity.avatarUrl} alt="" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-card bg-primary text-sm font-bold text-primary-foreground shadow-lg">
              {initials || '?'}
            </div>
          )}
        </div>

        {category && (
          <span
            className="absolute bottom-0 right-5 max-w-[65%] translate-y-1/2 truncate rounded-full bg-primary px-4 py-2 text-[12px] font-semibold text-primary-foreground shadow-lg"
            data-canonical-entity-category="true"
            title={category}
          >
            {category}
          </span>
        )}
      </div>

      <div className="px-5 pb-4 pt-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3
              className="line-clamp-2 text-[20px] font-bold leading-snug text-foreground"
              data-canonical-entity-title="true"
            >
              {title}
            </h3>
            {handle && <p className="mt-0.5 truncate text-sm text-muted-foreground">@{handle}</p>}
          </div>
          {flag && (
            <span
              className="shrink-0 text-xl"
              aria-label={`Origin: ${entity.countryCode?.toUpperCase()}`}
              title={`Origin: ${entity.countryCode?.toUpperCase()}`}
              data-canonical-entity-origin="true"
            >
              {flag}
            </span>
          )}
        </div>

        {about && (
          <p
            className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground"
            data-canonical-entity-about="true"
          >
            {about}
          </p>
        )}
      </div>

      {metrics.length > 0 && (
        <div
          className={cn(
            'grid divide-x divide-border border-t border-border bg-muted/30',
            gridColumns,
          )}
          data-canonical-entity-metrics="true"
        >
          {metrics.map((metric) => (
            <div
              key={metric.key}
              className="flex min-w-0 flex-col items-center justify-center px-2 py-3 text-center"
              data-entity-metric={metric.key}
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                {metric.label}
              </span>
              <span className="mt-0.5 truncate text-lg font-bold text-foreground">
                {metric.value}
              </span>
              {metric.note && (
                <span className="mt-0.5 truncate text-[9px] text-muted-foreground">
                  {metric.note}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {(secondaryAction || primaryAction) && (
        <div
          className={cn('grid gap-2 border-t border-border p-3', actionGridColumns)}
          data-canonical-entity-actions="true"
          data-no-nav
        >
          {secondaryAction && (
            <button
              type="button"
              disabled={secondaryAction.disabled}
              aria-label={secondaryAction.ariaLabel ?? secondaryAction.label}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                secondaryAction.onClick();
              }}
              className="min-h-11 touch-manipulation rounded-lg border border-border px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {secondaryAction.label}
            </button>
          )}

          {primaryAction && (
            <button
              type="button"
              disabled={primaryAction.disabled}
              aria-label={primaryAction.ariaLabel ?? primaryAction.label}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                primaryAction.onClick();
              }}
              className="min-h-11 touch-manipulation rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {primaryAction.label}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

/**
 * Public Katalag specialization of the canonical Entity Space shell.
 *
 * The wrapper owns only presentation policy:
 * - Katalag kind is fixed.
 * - Best category, origin, Reach, Kloutscore, and rank are rendered only when supplied.
 * - Follow and Make a deal are callback-driven; ownership and eligibility remain upstream.
 * - No View Katalag action is created. Opening the card body remains optional.
 */
export type CanonicalKatalagCardData = Omit<EntityCardData, 'kind'>;

export interface CanonicalKatalagCardProps {
  katalag: CanonicalKatalagCardData;
  onOpen?: (katalagId: string) => void;
  onMakeDeal?: (katalagId: string) => void;
  onFollow?: (katalagId: string) => void;
  makeDealDisabled?: boolean;
  followDisabled?: boolean;
  isFollowing?: boolean;
  className?: string;
}

export const CanonicalKatalagCard: React.FC<CanonicalKatalagCardProps> = ({
  katalag,
  onOpen,
  onMakeDeal,
  onFollow,
  makeDealDisabled = false,
  followDisabled = false,
  isFollowing = false,
  className,
}) => {
  const followAction: EntityCardAction | undefined = onFollow
    ? {
        label: isFollowing ? 'Following' : 'Follow',
        ariaLabel: isFollowing ? `Following ${katalag.title}` : `Follow ${katalag.title}`,
        disabled: followDisabled,
        onClick: () => onFollow(katalag.id),
      }
    : undefined;

  const makeDealAction: EntityCardAction | undefined = onMakeDeal
    ? {
        label: 'Make a deal',
        ariaLabel: `Make a deal with ${katalag.title}`,
        disabled: makeDealDisabled,
        onClick: () => onMakeDeal(katalag.id),
      }
    : undefined;

  return (
    <UniversalEntityCard
      entity={{
        ...katalag,
        kind: 'katalag',
        mediaFit: katalag.mediaFit ?? 'cover',
      }}
      onOpen={onOpen}
      secondaryAction={followAction}
      primaryAction={makeDealAction}
      className={className}
    />
  );
};

export default UniversalEntityCard;
